import { Tabs, FilterDrawer } from '~/components/ui';
import { ComboField } from '~/components/form';
import DateRange, { ALL_DATE_PRESETS } from '~/components/pages/ai/common/DateRange';
import { useGetAnalyticsFilterOptionsQuery } from '~/modules/query/ai/analytics';
import type {
  AnalyticsSource,
  AnalyticsTelemetrySortField,
  TelemetryStatus,
} from '~/modules/query/ai/types';
import {
  TELEMETRY_STATUSES,
  rangeForPreset,
  toIsoBoundary,
  toLocalDateValue,
  type AnalyticsDateRangePreset,
  type AnalyticsDateRangeValue,
} from '~/lib/ai/analytics';
import { parseEnum } from '~/lib/listParams';

// ── URL state ───────────────────────────────────────────────────────────────

/** The filter keys both tabs share, so a switch between them keeps the view (overview.md "Routes"). */
export const ANALYTICS_FILTER_KEYS = [
  'range',
  'start',
  'end',
  'entity',
  'model',
  'status',
] as const;

export interface AnalyticsScope {
  source: AnalyticsSource;
  dateRange: AnalyticsDateRangeValue;
  entity: string;
  model: string;
  status: TelemetryStatus | '';
  /** How many filters the Filters button counts: date range, entity, model, status. */
  activeCount: number;
}

export const DEFAULT_ANALYTICS_SORT: AnalyticsTelemetrySortField = 'promptEndAt';
const DEFAULT_PRESET: AnalyticsDateRangePreset = '30d';

const SOURCES = ['placement', 'digital_assistant'] as const;
const SOURCE_TABS = [
  { id: 'placement', label: 'Placements' },
  { id: 'digital_assistant', label: 'Digital assistants' },
] as const;

/** `/manager/analytics/overview` applies its own 30-day default when no range is sent, so "All time" and a custom range would both silently mean 30 days. */
export const OVERVIEW_DATE_PRESETS: AnalyticsDateRangePreset[] = ['7d', '30d'];

/** The server's sort fields; the session id column has none. */
export const TELEMETRY_SORT_FIELDS: AnalyticsTelemetrySortField[] = [
  'promptStartAt',
  'promptEndAt',
  'totalTokens',
  'estimatedCostUsd',
  'durationMs',
  'avgLatencyMs',
  'messageCount',
  'userName',
  'entityName',
  'model',
  'status',
];

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** The preset as stored, plus the explicit bounds while it is "Custom". */
function readDateRange(
  get: (key: string) => string | null,
  preset: AnalyticsDateRangePreset,
): AnalyticsDateRangeValue {
  const fallback = rangeForPreset(preset === '7d' ? '7d' : '30d');
  if (preset !== 'custom') return { preset, range: fallback };

  const start = get('start');
  const end = get('end');

  return {
    preset,
    range: {
      startDate:
        start && DATE_ONLY.test(start) ? toIsoBoundary(start, 'startDate') : fallback.startDate,
      endDate: end && DATE_ONLY.test(end) ? toIsoBoundary(end, 'endDate') : fallback.endDate,
    },
  };
}

/**
 * Reads the source and the shared filters, falling back per key so one bad value cannot blank the page.
 * `presets` is the set the current tab can honour; a range outside it reads as the default,
 * so the control and the query agree even when the URL carries another tab's preset.
 */
export function readAnalyticsScope(
  get: (key: string) => string | null,
  presets: AnalyticsDateRangePreset[] = ALL_DATE_PRESETS,
): AnalyticsScope {
  const preset = parseEnum(get('range'), presets, DEFAULT_PRESET);
  const entity = get('entity') ?? '';
  const model = get('model') ?? '';
  const status = parseEnum<TelemetryStatus | ''>(get('status'), TELEMETRY_STATUSES, '');

  return {
    source: parseEnum(get('source'), SOURCES, 'placement'),
    dateRange: readDateRange(get, preset),
    entity,
    model,
    status,
    activeCount:
      (preset !== DEFAULT_PRESET ? 1 : 0) + (entity ? 1 : 0) + (model ? 1 : 0) + (status ? 1 : 0),
  };
}

/** The URL patch for a date range; the default preset and the custom bounds are omitted. */
export function dateRangePatch(value: AnalyticsDateRangeValue): Record<string, string | null> {
  const patch: Record<string, string | null> = {
    range: value.preset === DEFAULT_PRESET ? null : value.preset,
    start: null,
    end: null,
  };

  if (value.preset === 'custom') {
    patch.start = toLocalDateValue(value.range.startDate);
    patch.end = toLocalDateValue(value.range.endDate);
  }

  return patch;
}

/** Source changed: an entity key from the other source is meaningless, so its dependents are dropped (overview.md). */
export const sourceChangePatch = (next: AnalyticsSource): Record<string, string | null> => ({
  source: next === 'placement' ? null : next,
  entity: null,
  model: null,
  session: null,
  page: null,
});

// ── Source pill tabs ────────────────────────────────────────────────────────

/** The one source control both tabs share: a source is never combined with the other. */
export function AnalyticsSourceTabs({
  value,
  onChange,
}: {
  value: AnalyticsSource;
  onChange: (next: AnalyticsSource) => void;
}) {
  return (
    <Tabs
      variant="pill"
      ariaLabel="Analytics source"
      idPrefix="ai-analytics-source"
      activeId={value}
      onChange={(id) => onChange(id as AnalyticsSource)}
      tabs={SOURCE_TABS.map((tab) => ({ id: tab.id, label: tab.label }))}
    />
  );
}

/** The panel `AnalyticsSourceTabs` controls. */
export function AnalyticsSourcePanel({
  source,
  className,
  children,
}: {
  source: AnalyticsSource;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      id={`ai-analytics-source-panel-${source}`}
      aria-labelledby={`ai-analytics-source-tab-${source}`}
      className={className}
    >
      {children}
    </div>
  );
}

// ── Filter drawer ───────────────────────────────────────────────────────────

type AnalyticsFilterValues = Record<string, string>;

interface AnalyticsFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  source: AnalyticsSource;
  /** The applied filters by URL key (`useListUrlState().filters`). */
  value: AnalyticsFilterValues;
  onApply: (next: AnalyticsFilterValues) => void;
  onClear: () => void;
  /** The overview endpoint has no status filter, so that tab hides the control rather than offering an inert one. */
  showStatus?: boolean;
  /** The presets the tab's endpoint can honour; defaults to all of them. */
  datePresets?: AnalyticsDateRangePreset[];
}

/** The filter tray both tabs share: date range, placement or assistant, AI model and (user data) status. */
export function AnalyticsFilterDrawer({
  open,
  onClose,
  source,
  value,
  onApply,
  onClear,
  showStatus = true,
  datePresets = ALL_DATE_PRESETS,
}: AnalyticsFilterDrawerProps) {
  const { data: options } = useGetAnalyticsFilterOptionsQuery({ source });

  const entityLabel = source === 'placement' ? 'Placement' : 'Digital assistant';
  const entityOptions = (options?.entities ?? []).map((entry) => ({
    value: entry.key,
    label: entry.name ?? entry.key,
  }));
  const modelOptions = (options?.models ?? []).map((entry) => ({
    value: entry.id,
    label: entry.id,
  }));

  return (
    <FilterDrawer<AnalyticsFilterValues>
      open={open}
      onClose={onClose}
      value={value}
      onApply={onApply}
      onClear={onClear}
      testId="ai-analytics-filters-drawer"
    >
      {(draft, patch) => (
        <>
          <DateRange
            value={readAnalyticsScope((key) => draft[key] || null, datePresets).dateRange}
            onChange={(next) =>
              patch(
                Object.fromEntries(
                  Object.entries(dateRangePatch(next)).map(([key, v]) => [key, v ?? '']),
                ),
              )
            }
            testId="ai-analytics-date-range"
            presets={datePresets}
          />
          <ComboField
            id="ai-analytics-filter-entity"
            testId="ai-analytics-filter-entity"
            label={entityLabel}
            value={draft.entity}
            options={[
              {
                value: '',
                label: source === 'placement' ? 'All placements' : 'All digital assistants',
              },
              ...entityOptions,
            ]}
            searchable
            onChange={(entity) => patch({ entity })}
          />
          <ComboField
            id="ai-analytics-filter-model"
            testId="ai-analytics-filter-model"
            label="AI model"
            value={draft.model}
            options={[{ value: '', label: 'All AI models' }, ...modelOptions]}
            searchable
            onChange={(model) => patch({ model })}
          />
          {showStatus ? (
            <ComboField
              id="ai-analytics-filter-status"
              testId="ai-analytics-filter-status"
              label="Status"
              value={draft.status}
              options={[
                { value: '', label: 'All statuses' },
                ...TELEMETRY_STATUSES.map((status) => ({ value: status, label: status })),
              ]}
              searchable={false}
              onChange={(status) => patch({ status })}
            />
          ) : null}
        </>
      )}
    </FilterDrawer>
  );
}
